import { FormEditIcon } from 'assets/icons';
import { CustomTextArea } from 'components';
import { UploadAttachmentToPresignedModal } from 'components';
import { PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect, useState } from 'react';
import {
  InvoiceAttachmentEditPage,
  addInvoiceAttachmentEditPage,
  addMultipleInvoiceAttachmentsEditPage,
  modifyInvoiceAttachmentEditPage,
  removeInvoiceAttachmentEditPage,
  resetInvoiceAttachementStateEditPage,
  selectEstimateIdForInvoiceEditPage,
  selectInvoiceAttachmentsEditPage,
  selectInvoiceNotesEditPage,
  selectJobDataForInvoiceEditPage,
  selectTNCEditPage,
  updateInvoiceNotesEditPage
} from '../invoicesEditSlice';
import AddAttachmentModal from 'containers/invoices/create-invoice/components/AddAttachmentModal';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { selectCurrentTeam } from 'containers/app/appSlice';
import ItemsTabReactIcon from 'assets/react-icons/ItemsTabReactIcon';
import AttachmentLibrary from 'components/attachment-library/AttachmentLibrary';
import { generateAttachmentSections } from 'containers/estimates/utils';
import { AttachmentFile, UploadAttachmentFile } from 'types/baseTypes';
import { useLazyGetEstimateAttachmentsQuery } from 'containers/estimates/api';
import { useLazyGetJobAttachmentListQuery } from 'containers/jobs/api';
import { notesMaxCount } from 'containers/settings/constants';

interface NotesAttachmentsSectionEditProps {
  className?: string;
  context: React.Context<any>;
  isLoadingTaxesAndFees: boolean;
}

const NotesAttachmentsSectionEdit: FC<NotesAttachmentsSectionEditProps> = ({ context, isLoadingTaxesAndFees }) => {
  const selectedInvoiceAttachments = useAppSelector(selectInvoiceAttachmentsEditPage);
  const selectedInvoiceComment = useAppSelector(selectInvoiceNotesEditPage);
  const selectedInvoiceTNC = useAppSelector(selectTNCEditPage);
  const selectedJobData = useAppSelector(selectJobDataForInvoiceEditPage);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const estimateId = useAppSelector(selectEstimateIdForInvoiceEditPage);
  const dispatch = useAppDispatch();
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.NOTES_ATTACHMENTS;

  const [getEstimateAttachmentsQuery, { data: estimateAttachments }] = useLazyGetEstimateAttachmentsQuery();

  const [getJobAttachmentListQuery, { data: jobAttachments }] = useLazyGetJobAttachmentListQuery();

  const initialAttachment = {
    id: null,
    url: null,
    title: null,
    imagePreview: null,
    description: null,
    urlLink: null
  } as InvoiceAttachmentEditPage;
  const [showAttachmentModal, setShowAttachmentModal] = useState<{
    open: boolean;
    attachment: InvoiceAttachmentEditPage;
    editMode: boolean;
  }>({
    open: false,
    attachment: initialAttachment,
    editMode: false
  });
  const [isAttachmentLibraryOpen, setIsAttachmentLibraryOpen] = useState(false);

  useEffect(() => {
    if (estimateId) {
      getEstimateAttachmentsQuery({ teamId: currentTeam.id, estimateId });
    }
  }, [estimateId]);

  useEffect(() => {
    if (selectedJobData?.[0]?.jobId) {
      getJobAttachmentListQuery({ jobId: selectedJobData?.[0]?.jobId, teamId: currentTeam.id });
    }
  }, [selectedJobData?.[0]?.jobId, currentTeam.id]);

  const onNotesChange = (value: string) => {
    dispatch(updateInvoiceNotesEditPage(value));
  };

  const handleAddPhotoClick = () => {
    updateSelectedInvoiceTab();
    setShowAttachmentModal({ open: true, editMode: false, attachment: initialAttachment });
  };

  const onSavePresignedAttachments = (attachments: UploadAttachmentFile[]) => {
    dispatch(addMultipleInvoiceAttachmentsEditPage(attachments));
    resetAttachmentModalState();
  };

  const resetAttachmentModalState = () => {
    setShowAttachmentModal(prevModalState => ({ ...prevModalState, open: false, editMode: false, attachment: null }));
  };

  const onSaveAttachmentClick = (attachment: InvoiceAttachmentEditPage) => {
    dispatch(addInvoiceAttachmentEditPage(attachment));
    resetAttachmentModalState();
  };

  const onModifyAttachmentClick = (attachment: InvoiceAttachmentEditPage) => {
    dispatch(modifyInvoiceAttachmentEditPage(attachment));
    resetAttachmentModalState();
  };

  const onRemoveAttachment = (attachment: InvoiceAttachmentEditPage) => {
    dispatch(removeInvoiceAttachmentEditPage(attachment));
  };

  const onEditAttachment = (attachment: InvoiceAttachmentEditPage) => {
    setShowAttachmentModal({ attachment: attachment, open: true, editMode: true });
  };

  const updateSelectedInvoiceTab = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.NOTES_ATTACHMENTS);
  };

  const handleOpenAttachmentLibrary = () => {
    setIsAttachmentLibraryOpen(true);
  };

  const handleCloseAttachmentLibrary = () => {
    setIsAttachmentLibraryOpen(false);
  };

  const onAddAttachmentsFromLibrary = attachments => {
    dispatch(resetInvoiceAttachementStateEditPage());
    attachments.forEach(attachment => {
      dispatch(
        addInvoiceAttachmentEditPage({ ...attachment, imagePreview: attachment.imagePreview ?? attachment.urlLink })
      );
    });
    handleCloseAttachmentLibrary();
  };

  return (
    <>
      <div>
        <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
          Notes & Attachments
        </div>
        <div
          className={`flex flex-col rounded-md  border px-[30px] py-5 1439px:px-10 1439px:pt-9 
            1439px:pb-10 ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
          <div className="mb-2 text-17px font-semibold text-heading">Notes</div>
          <CustomTextArea
            resizeTextArea={true}
            id="edit-invoice-notes"
            name="notes"
            maxLength={notesMaxCount}
            placeholder="Enter notes here"
            value={selectedInvoiceComment}
            onChange={onNotesChange}
            className="mb-4 py-[15px]"
            onFocus={updateSelectedInvoiceTab}
          />

          {!isLoadingTaxesAndFees && selectedInvoiceTNC.tnc && (
            <div className="mt-6 mb-4">
              <div className="mb-4 text-17px font-semibold text-headingGray">Terms & Conditions</div>
              <div
                className=" h-[100px] overflow-y-hidden rounded border border-borderGray 
            pt-2.5 text-17px font-normal text-primaryText">
                <div className="customNormalScroll max-h-[80px] overflow-y-auto whitespace-pre-line break-words px-5">
                  {selectedInvoiceTNC.tnc}
                </div>
              </div>
            </div>
          )}
          <div className="mb-2 text-17px font-semibold text-heading">Attachments</div>

          <div className="mb-5 flex flex-col gap-5">
            {selectedInvoiceAttachments?.map(attachment => {
              return (
                <div
                  onClick={() => onEditAttachment(attachment)}
                  key={attachment.url}
                  className="relative flex min-h-[100px] w-full flex-row rounded border border-borderGray py-4 px-5">
                  <div className="relative mr-4 h-16 w-16 rounded-md">
                    <img
                      className="absolute left-1/2 h-full -translate-x-1/2 rounded-md object-cover"
                      src={attachment.imagePreview}
                    />
                  </div>

                  <div className="mt-[2px] flex w-1/2 flex-col">
                    <div className="mb-[6px] break-words text-17px font-semibold text-heading line-clamp-2">
                      {attachment.title}
                    </div>
                    <div className="whitespace-pre-line text-sbase font-normal text-heading line-clamp-3">
                      {attachment?.description}
                    </div>
                  </div>
                  <FormCloseReactIcon
                    id="edit-invoice-remove-attachment-close-icon"
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveAttachment(attachment);
                    }}
                    className="absolute top-3 right-[-25px] shrink-0 cursor-pointer 1439px:right-[-30px]"
                  />
                  <FormEditIcon
                    id="edit-invoice-attachment-edit-icon"
                    onClick={() => onEditAttachment(attachment)}
                    className="absolute bottom-3 right-[-25px] mt-3 shrink-0 cursor-pointer 1439px:right-[-30px]"
                  />
                </div>
              );
            })}
          </div>

          <div
            className="flex w-full cursor-pointer flex-row items-center 
            justify-between rounded border border-borderGray py-4 px-5">
            <div className="flex flex-row gap-2" onClick={handleAddPhotoClick}>
              <LineItemPlusReactIcon id="create-invoice-add-photo-icon" className="shrink-0" />
              <div id="create-invoice-add-photo" className="text-17px font-semibold text-secondary">
                Add photos
              </div>
            </div>
            {selectedJobData.length > 0 && (
              <div className="flex cursor-pointer items-center gap-2" onClick={handleOpenAttachmentLibrary}>
                <ItemsTabReactIcon
                  id="add-from-library"
                  className="path-fill-current rect-stroke-current mr-2 text-secondary"
                />
                <div className="text-17px font-semibold text-secondary">Add from library</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAttachmentModal.open && !showAttachmentModal.editMode && (
        <UploadAttachmentToPresignedModal
          open={showAttachmentModal.open}
          toggleOpen={resetAttachmentModalState}
          onSave={onSavePresignedAttachments}
          useCase={PRESIGNED_UPLOAD_USE_CASE.INVOICE}
          idPrefix="edit-invoice-upload-attachment"
        />
      )}

      {showAttachmentModal.open && showAttachmentModal.editMode && (
        <AddAttachmentModal
          onModifyAttachmentClick={onModifyAttachmentClick}
          onSaveAttachmentClick={onSaveAttachmentClick}
          toggleOpen={resetAttachmentModalState}
          open={showAttachmentModal.open}
          isEdit={showAttachmentModal.editMode}
          attachmentData={showAttachmentModal.attachment}
          isEditInvoiceFlow={true}
        />
      )}

      {isAttachmentLibraryOpen && (
        <AttachmentLibrary
          onClose={() => setIsAttachmentLibraryOpen(false)}
          onSave={onAddAttachmentsFromLibrary}
          isOpen={isAttachmentLibraryOpen}
          sections={generateAttachmentSections(estimateAttachments, jobAttachments, null)}
          selectedAttachments={selectedInvoiceAttachments as AttachmentFile[]}
        />
      )}
    </>
  );
};

export default NotesAttachmentsSectionEdit;
